import { noop } from 'lodash';
import { memo, useCallback } from 'react';

import { useTrackAppliedSearchTerm } from '@air/analytics';
import { useBreakpointsContext } from '@air/provider-media-query';

import { getQueryWithNullAssetModalParams } from '~/components/AssetModalV2/utils';
import { PublicShowcaseSearchActions } from '~/components/Search/PublicShowcaseSearchActions';
import { MobileSearchCommand } from '~/components/SearchCommand/MobileSearchCommand';
import { SearchCommand, type SearchCommandProps } from '~/components/SearchCommand/SearchCommand';
import { SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM } from '~/components/v2/components/showcase/showcaseSubPageBoardContext';
import { QueryParamNames } from '~/constants/search';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { pushWithExistingQuery } from '~/utils/PathUtils';

export type PublicShowcaseSearchProps = {
  onCloseClick?: () => void;
};

export const PublicShowcaseSearch = memo(({ onCloseClick }: PublicShowcaseSearchProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { search, setSearch } = useSearchParams();
  // The record the collection tree was mounted with, never a fetch — the same source the tree's
  // other name-carrying leaves read (breadcrumbs, empty-results state).
  const { collection, getPageNavigationHref } = usePublicShowcaseContext();
  const { trackAppliedSearchTerm } = useTrackAppliedSearchTerm();

  const renderActions = useCallback<Required<SearchCommandProps>['renderActions']>(
    ({ onClearSearchValue }) => <PublicShowcaseSearchActions onClearSearchValue={onClearSearchValue} />,
    [],
  );

  const onSuggestionClick = useCallback(
    (suggestion: string) => {
      // FOUND-584: a collection link has ONE search results page — the link's root route with the
      // term — and a search always covers the WHOLE showcase. So committing a term from ANY
      // URL-backed surface (the root page, a sub-page, a drilled board) lands there, in one push
      // that carries the root path AND the term together (two pushes would race). On the root page
      // that push is the same URL an in-place search would produce; from a sub-page or a drilled
      // board it also exits the page/drill, since both derive from the route. The one fall-through
      // is a surface whose navigation has no URLs (no getPageNavigationHref — the collection link
      // editor's Edit tab): with no top-level route to jump to, it keeps searching in place.
      //
      // The jump has to shed the route's page context, not just change the path: a board drilled out
      // of a sub-page records that page in its query string, and `pushWithExistingQuery` carries the
      // standing query into the push — so the trail param would re-select the sub-page ON the
      // top-level route, leaving results page-scoped and stranding a viewer who clears the search on
      // a root path that still resolves as the sub-page. A nil value deletes the param, so the same
      // single push drops the trail.
      //
      // The root path also drops any `/c/{clipId}` segment, so the jump CLOSES an open asset modal
      // (the modal mounts off the path) — and it must clean up after it the way the modal's own close
      // does, or the standing modal params ride along and get re-applied to the next asset the viewer
      // opens from the results page. The search bar is reachable with the modal open: the header
      // outlives it in the DOM, and an immersive link even paints the header above the overlay.
      // That cleanup happens to null `page` too — the PDF-page param shares the trail param's literal
      // name — so the trail drop above stays explicit rather than resting on a name collision.
      if (suggestion && getPageNavigationHref) {
        trackAppliedSearchTerm({ term: suggestion, search_type: 'exact' });
        pushWithExistingQuery({
          path: getPageNavigationHref({ pageSlug: null }),
          newQuery: {
            ...getQueryWithNullAssetModalParams(),
            [SHOWCASE_SUB_PAGE_BOARD_QUERY_PARAM]: undefined,
            [QueryParamNames.Search]: suggestion,
          },
        });

        return;
      }

      void setSearch(suggestion);
    },
    [getPageNavigationHref, setSearch, trackAppliedSearchTerm],
  );

  // FOUND-516: what a viewer searches is THIS showcase, so the field names it — the same copy board
  // surfaces use ("Search {board name}"). The name is guaranteed: the record is required and Save is
  // gated on a non-empty name, so there is no unnamed state to fall back for.
  const placeholder = `Search ${collection.name}`;

  if (isAboveMediumScreen) {
    return (
      <SearchCommand
        onClear={isAboveMediumScreen ? undefined : onCloseClick}
        onOpenChange={noop}
        onSearchChange={noop}
        onSuggestionClick={onSuggestionClick}
        placeholder={placeholder}
        search={search}
        suggestions={undefined}
        renderActions={renderActions}
      />
    );
  }

  return (
    <MobileSearchCommand
      onClear={isAboveMediumScreen ? undefined : onCloseClick}
      onOpenChange={noop}
      onSearchChange={noop}
      onSuggestionClick={onSuggestionClick}
      placeholder={placeholder}
      search={search}
      suggestions={undefined}
    />
  );
});

PublicShowcaseSearch.displayName = 'PublicShowcaseSearch';
